import { useContext, useEffect, useState } from 'react';
import { FormInput } from './FormInput';
import { PhoneNumberUtil } from 'google-libphonenumber';
import GoogleAddress, { AddressInfo } from './GoogleAddress';
import EnphaseContext from './../../../contexts/EnphaseContext';
import AppContext from './../../../contexts/AppContext';
import DataContext from './../../../contexts/DataContext';
import ConfigContext from './../../../contexts/ConfigContext';
import { useNavigate } from 'react-router-dom';
import {
  formatPhoneNumber,
  registerGoogleSolarResponse,
} from './../../../helpers';
import { DateTime } from 'luxon';

import 'rc-slider/assets/index.css';
import Slider from 'rc-slider';

import CustomSelect from '../../../components/CustomSelect';
import { postTimeMeta } from '../../../helpers';

export const Form = () => {
  const { getInitialEnphaseData } = useContext(EnphaseContext);
  const {
    enphaseAccTariffs,
    urlParams,
    setIsShortISRFeatureFormCustomer,
    slfApiUrl,
    ISRAppointmentDetails,
    latLng,
  } = useContext(AppContext);
  const { data, updateData } = useContext(DataContext);
  const { testName } = useContext(ConfigContext);
  const navigate = useNavigate();

  const phoneUtil = PhoneNumberUtil.getInstance();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [fullAddress, setFullAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [isAddressValid, setIsAddressValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorSubmitting, setErrorSubmitting] = useState(false);
  const [interestedOptions, setInterestedOptions] = useState([
    { key: 'add_solar', option: 'Add more solar', selected: false },
    { key: 'add_battery', option: 'Add battery', selected: false },
    { key: 'add_ev_charger', option: 'Add an EV charger', selected: false },
    {
      key: 'upgrade_microinverters',
      option: 'Upgrade microinverters',
      selected: false,
    },
    { key: 'support', option: 'Technical support', selected: false },
  ]);
  const [ownHome, setOwnHome] = useState<boolean | null>(null);
  const [monthlyBill, setMonthlyBill] = useState<number>(250);
  const [utilityProvider, setUtilityProvider] = useState<any>(null);
  const [providerOptions, setProviderOptions] = useState([]);
  const [havePanels, setHavePanels] = useState<boolean | null>(null);
  const [loadingUtilityProviders, setLoadingUtilityProviders] = useState(false);

  const toggleOption = (key: string) => {
    const newOptions = [...interestedOptions];
    const option = newOptions.find((o) => o.key === key);
    if (option) {
      option.selected = !option.selected;
      setInterestedOptions(newOptions);
    }
  };

  const isValidName = (value: string): boolean => {
    return value.trim().length >= 2;
  };

  const isValidEmail = (email: string): boolean => {
    return !!String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      )?.length;
  };

  const isValidPhone = (phone: string) => {
    try {
      return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
    } catch (error) {
      return false;
    }
  };

  const handleAddress = (addressInfo: AddressInfo) => {
    setIsAddressValid(addressInfo.isValid);

    if (addressInfo.isValid) {
      setStreet(addressInfo.components.street);
      setCity(addressInfo.components.city);
      setState(addressInfo.components.state);
      setPostcode(addressInfo.components.postalCode);
      setFullAddress(addressInfo.fullAddress);
    }
  };

  const isValidForm = () => {
    const userData =
      firstName && lastName && isValidPhone(phone) && email && isAddressValid;
    const existingCustomer =
      havePanels && interestedOptions.some((option) => option.selected);
    const newCustomer =
      !havePanels && (ownHome === true || ownHome === false) && utilityProvider;
    return userData && (existingCustomer || newCustomer);
  };

  const handleUtilityChange = (selectedOption: any) => {
    setUtilityProvider(selectedOption);
  };

  const fillUtilityProviderOptions = () => {
    if (Object.keys(enphaseAccTariffs).length === 0) return;
    const options: any = [];
    enphaseAccTariffs.plans.forEach((option: any) => {
      options.push({ value: option.lseName, label: option.lseName });
    });
    setProviderOptions(options);
    const selectedOption: any = {
      value: enphaseAccTariffs.prefLseName,
      label: enphaseAccTariffs.prefLseName,
    };
    setUtilityProvider(selectedOption);
    setLoadingUtilityProviders(false);
  };

  useEffect(() => {
    if (isAddressValid) {
      setLoadingUtilityProviders(true);
      getInitialEnphaseData(fullAddress, postcode);
    }
  }, [isAddressValid]);

  useEffect(() => {
    fillUtilityProviderOptions();
  }, [enphaseAccTariffs]);

  return (
    <div>
      <div className="bg-white p-4 rounded-3xl text-black text-left">
        <div className="flex flex-col lg:flex-row">
          <div className="w-full lg:w-1/2 p-4 bg-white lg:border-r border-b border-black">
            <FormInput
              value={firstName}
              onChange={setFirstName}
              haveError={!isValidName(firstName)}
              id="first-name"
              labelKey="firstName"
              label="First name"
              placeholder="First name"
            />
          </div>
          <div className="w-full lg:w-1/2 p-4 bg-white border-b border-black">
            <FormInput
              value={lastName}
              onChange={setLastName}
              haveError={!isValidName(lastName)}
              id="last-name"
              labelKey="lastName"
              label="Last name"
              placeholder="Last name"
            />
          </div>
        </div>
        <div className="flex flex-col lg:flex-row">
          <div className="w-full lg:w-1/2 p-4 bg-white lg:border-r border-b border-black">
            <FormInput
              type="phone"
              value={phone}
              onChange={setPhone}
              haveError={!isValidPhone(phone)}
              labelKey="phone"
              label="Phone number"
              placeholder="Phone number"
              country="US"
            />
          </div>
          <div className="w-full lg:w-1/2 p-4 bg-white overflow-hidden border-b border-black">
            <FormInput
              type="email"
              value={email}
              onChange={setEmail}
              haveError={!isValidEmail(email)}
              id="email"
              labelKey="email"
              label="Email"
              placeholder="Email"
            />
          </div>
        </div>
        <div className="flex flex-col lg:flex-row">
          <div className="w-full p-4 bg-white">
            <GoogleAddress
              onAddressSelected={handleAddress}
              selectedCountry="US"
            />
          </div>
        </div>
      </div>

      <div className="mt-8 text-[18px] px-6">
        <p>Do you already have solar panels on your roof?</p>
        <div className="flex gap-8 mt-4">
          <RadioButton
            label="Yes"
            selected={havePanels === true}
            onClick={() => setHavePanels(true)}></RadioButton>
          <RadioButton
            label="No"
            selected={havePanels === false}
            onClick={() => setHavePanels(false)}></RadioButton>
        </div>
      </div>

      <div
        className={`text-[18px] px-6 ${
          havePanels ? 'h-auto mt-6 opacity-100' : 'h-0 opacity-0'
        } overflow-hidden transition-all duration-500`}>
        <p>What are you interested in today? (select all that apply):</p>
        <div className="mt-4">
          {interestedOptions.map((option, index) => (
            <CheckBox
              key={index}
              label={option.option}
              selected={option.selected}
              onClick={() => toggleOption(option.key)}></CheckBox>
          ))}
        </div>
      </div>

      <div
        className={`text-[18px] flex flex-col px-6 ${
          havePanels === false ? 'h-auto mt-6 opacity-100' : 'h-0 opacity-0'
        } overflow-hidden transition-all duration-500`}>
        <div>
          <p>
            <b>Great, just 3 more questions to view your solar savings!</b>
          </p>
          <p className="mt-6">Do you own or rent?</p>
          <div className="mt-4">
            <RadioButton
              label="Own"
              selected={ownHome === true}
              onClick={() => {
                setOwnHome(true);
              }}></RadioButton>
            <RadioButton
              label="Rent"
              selected={ownHome === false}
              onClick={() => {
                setOwnHome(false);
              }}></RadioButton>
          </div>

          <p className="mt-8">Who is your utility provider?</p>
          <div className="h-10 mt-4 max-w-[400px] z-10 relative">
            {loadingUtilityProviders ? (
              <p className="text-[14px] text-gray-600">
                <span className="loader-dots"></span>
              </p>
            ) : (
              <CustomSelect
                value={utilityProvider}
                onChange={handleUtilityChange}
                options={providerOptions}
                placeholder="Select your utility provider"
              />
            )}
          </div>

          <p className="mt-8">Tell us your average monthly electricity bill?</p>
          <div className="select-none mb-4">
            <div className="mt-[60px] flex justify-between flex-col md:flex-row">
              <div className="max-w-[279px] md:w-[400px] md:max-w-none relative">
                <Slider
                  value={monthlyBill}
                  min={50}
                  max={850}
                  onChange={(value) => {
                    setMonthlyBill(value as number);
                  }}
                  step={25}
                  className="short-isr"
                />
                <SliderTooltip value={monthlyBill} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`mt-12 px-6`}>
        <div className="text-[12px]">
          <label>
            <input type="hidden" id="leadid_tcpa_disclosure" />* We’re here to
            help! Each time that you fill out this form, you agree to be
            contacted by phone call, pre-recorded voice, and/or text message at
            the telephone or mobile number that you entered above by Enphase
            Energy, Inc. and by{' '}
            <a
              href="https://start.enphase.com/solar-installers/"
              target="_blank"
              rel="noreferrer"
              className="underline">
              one (1) other company
            </a>{' '}
            about solar and energy storage products and services. These
            calls/messages may be sent using automated telephone technology,
            even if your telephone or mobile number is currently listed on any
            state, federal or corporate “Do Not Call” list. Message and data
            rates may apply. You are not required to give your consent here as a
            condition of any purchase.
          </label>{' '}
          I understand the{' '}
          <a
            href="https://enphase.com/legal/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="underline">
            Privacy Policy
          </a>{' '}
          and agree to the{' '}
          <a
            href="https://enphase.com/legal/terms-of-service"
            target="_blank"
            rel="noreferrer"
            className="underline">
            Terms & Conditions
          </a>
          .
        </div>
      </div>

      {errorSubmitting && (
        <div className="text-enph-red-functional bg-enph-grey-04 mt-10 rounded-xl p-6">
          <p>
            Oops, something went wrong when submitting the information, please
            try again.
          </p>
        </div>
      )}

      <button
        className="w-full bg-black rounded-full text-white py-4 mt-6 text-[16px] hover:text-black hover:bg-enph-orange disabled:cursor-default disabled:text-gray-300 disabled:bg-gray-400"
        disabled={!isValidForm()}
        onClick={() => {
          const existingCustomer =
            havePanels && interestedOptions.some((option) => option.selected);

          if (existingCustomer || ISRAppointmentDetails) {
            // ISRAppointmentDetails -> User already set an appointment
            const requestData: any = {
              street,
              city,
              state,
              zip: postcode,
              monthly_elec: monthlyBill,
              monthly_elec_origin: monthlyBill,
              universal_leadid: (document.getElementById('leadid_token') as any)
                ?.value,
              country: 'US',
              residence_ownership: existingCustomer
                ? 'Own'
                : ownHome
                ? 'Own'
                : 'Rent',
              first_name: firstName,
              last_name: lastName,
              phone_number: formatPhoneNumber(phone.replace('+1', '')),
              'lp-type': 'ussp-multi-form',
              property_type: 'Single Family Home',
              email,
              shade: 'No Shade',
              human_check: '',
              xxTrustedFormToken: (
                document.getElementById('xxTrustedFormToken_0') as any
              )?.value,
              xxTrustedFormCertUrl: (
                document.getElementById('xxTrustedFormCertUrl_0') as any
              )?.value,
              xxTrustedFormPingUrl: (
                document.getElementById('xxTrustedFormPingUrl_0') as any
              )?.value,
              electric_utility: utilityProvider?.value,
              f: testName !== 'default' ? 'ab_test_' + testName : null,
              referral_code: null,
              ownership_status: 'on',
              'email-report3': 0,
              landing_page: process.env.REACT_APP_ROOT_PATH!.replace('/', ''),
              address: `${fullAddress}, ${city}, ${state.toUpperCase()} ${postcode}`,
              full_name: `${firstName} ${lastName}`,
              'meta:enphase_branded': 1,
              time: '',
              timezone: '',
              sandbox: '1',
            };

            if (requestData.first_name === 'passQC') {
              requestData.zip = 99999;
            }

            const interestedInArray: string[] = [];
            if (existingCustomer) {
              interestedOptions.forEach((option) => {
                if (option.selected) interestedInArray.push(option.key);
              });
              requestData.interested_in = interestedInArray.join('+');
            }

            const formData = new FormData();
            for (let key in requestData) {
              formData.append(key, requestData[key]);
            }

            const postUrl = `${slfApiUrl}/signup/cpa3${urlParams}${
              testName !== 'default' ? '&f=ab_test_' + testName : ''
            }`;

            (async () => {
              let response;
              try {
                response = await fetch(postUrl, {
                  method: 'POST',
                  body: formData,
                });
                // Dev server returns a html response with warnings, so instead of reading the json response we get the text
                const resultText = await response.text();
                let result: any = {};
                try {
                  result = JSON.parse(resultText);
                } catch (e) {
                  const jsonMatches = resultText.match(/{".+"}/g);
                  if (jsonMatches) result = JSON.parse(jsonMatches[0]);
                }
                updateData({
                  ...data,
                  leadId: result.lead_id,
                  cpi: result.cpi,
                  selectedAddress: street,
                  selectedCity: city,
                  selectedState: state,
                  selectedZipCode: postcode,
                  bill: monthlyBill,
                  addressOwnership: 'Own',
                  firstName,
                  lastName,
                  fullName: `${firstName} ${lastName}`,
                  phone: formatPhoneNumber(phone.replace('+1', '')),
                  property: 'Single Family Home',
                  email,
                  sunlight: 'No Shade',
                  utilityProvider: utilityProvider?.value,
                });
                window.leadId = result.lead_id;
                window.havePanels = havePanels;

                const haveSolar = existingCustomer ? 'yes' : 'no';
                const haveSolarUrl = `${slfApiUrl}/api/user/update_lead_meta/${result.lead_id}/expop_have_solar/${haveSolar}`;
                fetch(haveSolarUrl);

                if (existingCustomer) {
                  let metadataUrl = `${slfApiUrl}/api/user/update_lead_meta/${
                    result.lead_id
                  }/interested_in/${encodeURIComponent(
                    interestedInArray.join('+')
                  )}`;
                  fetch(metadataUrl);
                }

                postTimeMeta(slfApiUrl, result.lead_id);

                if (ISRAppointmentDetails) {
                  let updateUserMetaUrl = `${slfApiUrl}/api/user/update_lead_meta/${
                    window.leadId
                  }/appointment_host_name/${encodeURIComponent(
                    ISRAppointmentDetails.userName
                  )}`;
                  fetch(updateUserMetaUrl);
                  updateUserMetaUrl = `${slfApiUrl}/api/user/update_lead_meta/${
                    window.leadId
                  }/appointmentDate/${encodeURIComponent(
                    DateTime.fromISO(
                      ISRAppointmentDetails.startTime
                    ).toLocaleString({
                      weekday: 'long',
                      month: 'long',
                      day: '2-digit',
                    })
                  )}`;
                  fetch(updateUserMetaUrl);
                  updateUserMetaUrl = `${slfApiUrl}/api/user/update_lead_meta/${
                    window.leadId
                  }/appointment_time/${encodeURIComponent(
                    DateTime.fromISO(
                      ISRAppointmentDetails.startTime
                    ).toLocaleString({
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  )}`;
                  fetch(updateUserMetaUrl);
                  updateUserMetaUrl = `${slfApiUrl}/api/user/update_lead_meta/${window.leadId}/set_calendly_appointment/1`;
                  fetch(updateUserMetaUrl);
                  updateUserMetaUrl = `${slfApiUrl}/api/user/update_lead_meta/${window.leadId}/sandbox_end/1`;
                  fetch(updateUserMetaUrl);
                }

                (async () => {
                  try {
                    const latLngShadeResponse = await fetch(
                      `https://ztfs7b6dubqgheleincjlrqt540komou.lambda-url.us-east-1.on.aws/?lat=${latLng.lat}&lng=${latLng.lng}`
                    );
                    const latLngShade = await latLngShadeResponse.json();
                    registerGoogleSolarResponse(
                      slfApiUrl,
                      window.leadId,
                      latLngShade
                    );
                  } catch {}
                })();
              } catch (error: any) {}
            })();

            setIsShortISRFeatureFormCustomer(true);
            const root = document.documentElement;
            root.classList.add('lander-concept');
            setTimeout(
              () => window.scrollTo({ top: 0, behavior: 'smooth' }),
              0
            );
          } else {
            updateData({
              ...data,
              selectedAddress: street,
              selectedCity: city,
              selectedState: state,
              selectedZipCode: postcode,
              bill: monthlyBill,
              addressOwnership: havePanels ? 'Own' : ownHome ? 'Own' : 'Rent',
              firstName,
              lastName,
              fullName: `${firstName} ${lastName}`,
              phone: formatPhoneNumber(phone.replace('+1', '')),
              property: 'Single Family Home',
              email,
              sunlight: 'No Shade',
              utilityProvider: utilityProvider?.value,
            });

            navigate(`/calculating${urlParams}`);
          }
        }}>
        {havePanels ? 'Book an appointment' : 'Submit'}
      </button>
      <a
        href="tel:+15109456752"
        className="inline-block text-center w-full bg-[#dcdcd6] rounded-full py-4 mt-6 text-[16px] border border-black hover:bg-enph-orange hover:border-enph-orange">
        Call an energy advisor now
      </a>
    </div>
  );
};

const RadioButton = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: any;
}) => {
  return (
    <div
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className={`text-[16px] my-2`}>
      {label}
    </div>
  );
};

const CheckBox = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: any;
}) => {
  return (
    <label
      className={`text-[16px] my-2 relative pl-9 cursor-pointer select-none flex items-center`}>
      {label}
      <input
        type="checkbox"
        checked={selected}
        className="cursor-pointer absolute opacity-0 h-0 w-0"
        onClick={onClick}
      />
      <span className="absolute top-0 left-0 h-5 w-5 bg-white border border-black rounded-md"></span>
      <div
        className={`absolute ${
          selected ? 'block' : 'hidden'
        } left-[7px] top-[3px] w-[6px] h-3 border border-black border-l-0  border-t-0 rotate-45`}></div>
    </label>
  );
};

interface SliderTooltipProps {
  value: number;
}

const SliderTooltip = ({ value }: SliderTooltipProps) => {
  const leftPosition = (value / 800) * 100 + '%';
  return (
    <div
      className="absolute text-[14px] text-white bg-[#3C3C3C] p-2 rounded min-w-[50px] text-center mt-[-65px] ml-[-41px] md:ml-[-50px]"
      style={{
        left: leftPosition,
        lineHeight: 1,
      }}>
      {`$${value}`}
      <span className="arrow-down"></span>
    </div>
  );
};
